import { useEffect, useRef, useState } from "react";

export function useTaskTitleEditing() {
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    function startEditingTitle() {
        setIsEditingTitle(true);
    }

    function stopEditingTitle() {
        setIsEditingTitle(false);
    }

    return { isEditingTitle, startEditingTitle, stopEditingTitle };
}

/**
 * Ends the title where the task's main column ends, so the header lines up with the sections below it. The
 * header and the content are laid out apart, with their own padding, and the header also holds the pane's
 * controls, so the only width the two share is the one on screen.
 * The width is written straight onto the title instead of going through state: a pane is resized by dragging
 * it in the rail, and a render of the whole task on every step of the drag would be felt.
 */
export function useTitleAlignedWithMainColumn(isSingleColumn: boolean, isContentShown: boolean) {
    const titleRef = useRef<HTMLDivElement>(null);
    const mainColumnRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const title = titleRef.current;
        const mainColumn = mainColumnRef.current;
        // Stacked, the main column already spans the pane, so there is nothing to line up with.
        if (!title || !mainColumn || isSingleColumn) {
            return;
        }

        const alignTitle = () => {
            const width = mainColumn.getBoundingClientRect().right - title.getBoundingClientRect().left;
            // A pane folded away measures nothing, and holding the title to that would hide it.
            title.style.maxWidth = width > 0 ? `${width}px` : "";
        };

        alignTitle();
        // The title's own area, not the title, is observed: the title's size follows from the width set on it.
        const observer = new ResizeObserver(alignTitle);
        observer.observe(mainColumn);
        observer.observe(title.parentElement ?? title);

        return () => {
            observer.disconnect();
            title.style.maxWidth = "";
        };
    }, [isSingleColumn, isContentShown]);

    return { titleRef, mainColumnRef };
}
